//@ts-nocheck
import { useState, useEffect } from "react";
import { Table, Button, Input, Select } from "antd";
import { LeftOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import AddCategoryModal from "./addCategory";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";

import {
  getCategories,
  deleteCategory,
  getCategoryById,
} from "../../utils/API";

// --- Types ---
interface CategoryData {
  key: string;
  category: string;
  subCategory: string;
}

const CategoryMaster = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataSource, setDataSource] = useState<CategoryData[]>([]);
  const [editData, setEditData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  // 🔥 FETCH DATA FROM API
  const fetchData = async (page = 1) => {
    try {
      const res = await getCategories(page);

      const formatted = res.data.map((cat: any) => ({
        key: cat.catId,
        category: cat.catName,
        subCategory: cat.SubCategories?.map((s: any) => s.subCatName).join(
          ", ",
        ),
      }));

      setDataSource(formatted);
      setTotal(res.total);
      setCurrentPage(res.page);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  // ✏️ EDIT
  const handleEdit = async (id: number) => {
    const data = await getCategoryById(id);
    setEditData(data);
    setIsModalOpen(true);
  };

  // 🗑 DELETE
  const handleDelete = async (id: number) => {
    await deleteCategory(id);
    fetchData(currentPage);
  };

  // --- Table Column Definitions ---
  const columns: ColumnsType<CategoryData> = [
    {
      title: "#",
      key: "index",
      align: "left",
      width: "20%",
      render: (_, __, index) => index + 1,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingLeft: "40px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingLeft: "40px", fontWeight: "400" },
      }),
    },
    {
      title: "Categories",
      dataIndex: "category",
      key: "category",
      align: "left",
      width: "40%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
    },
    {
      title: "Sub Categories",
      dataIndex: "subCategory",
      key: "subCategory",
      align: "left",
      width: "40%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingRight: "60px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingRight: "60px", fontWeight: "400" },
      }),
      render: (record) => (
        <div className="flex justify-center gap-4">
          <LiaEditSolid
            className="text-gray-800 cursor-pointer hover:text-blue-600 text-lg"
            onClick={() => handleEdit(record.key)}
          />
          <RiDeleteBinLine
            className="text-gray-600 cursor-pointer hover:text-red-600 text-lg"
            onClick={() => handleDelete(record.key)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 bg-slate-50 min-h-screen">
      <div className="flex justify-end mb-2">
        <PageLogoutButton />
      </div>
      {/* Header */}
      <div className="flex justify-between items-center p-2 mb-4 gap-2">
        <div className="flex items-center gap-4">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center border-gray-300"
            onClick={() => navigate("/dashboard")}
          />

          <NavigationDropdown />

          <h1 className="text-base sm:text-xl font-normal text-gray-700">
            Category
          </h1>
        </div>

        <div className="flex flex-1 justify-end gap-4 items-center">
          <Input
            prefix={<SearchOutlined className="text-xl" />}
            placeholder="Search ...."
            className="rounded-lg h-10 border-gray-300 flex-1 max-w-xs"
          />
          <Button
            type="primary"
            className="bg-[#004aad] hover:bg-blue-800 rounded-lg border-none flex items-center justify-center font-light flex-shrink-0 text-white h-8 w-8 sm:h-10 sm:w-auto sm:px-6"
            onClick={() => {
              setEditData(null); // ➕ Add mode
              setIsModalOpen(true);
            }}
          >
            <PlusOutlined className="text-base sm:text-lg" />
            <span className="!hidden sm:!inline ml-2">Add</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[24px] border border-gray-200 p-6 shadow-sm">
        <div className="rounded-xl overflow-hidden mx-3">
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={{
              current: currentPage,
              total: total,
              pageSize: 10,
              onChange: (page) => setCurrentPage(page),
            }}
            className="custom-table"
          />
        </div>
      </div>

      {/* Modal */}
      <AddCategoryModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        refresh={fetchData}
        editData={editData}
      />
    </div>
  );
};

export default CategoryMaster;
